import { PureComponent, createRef } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import autoBind from 'react-autobind';
import { isMobile } from 'react-device-detect';
import loadable from '@utils/loadable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { Link } from 'react-router-dom';

import { setActiveHref } from '../../../actions/leftBar.actions';
import withComponentName from '../../../decorators/withComponentName';

import { getMetaTag } from '../../../helpers/helpers';
import { sourceDataHelper } from '../../../helpers/filingsHelpers';
import FilingsSvc from '../../../services/dbServices/FilingsSvc';
import { UK_INSIGHTS } from '../../../data/features';
import { UK_PRIVATE_COMPANY_INSIGHTS_PATH_MAIN } from '../../../data/ukPrivateCompanyInsights';

import Preloader from '../../Preloader';
import Breadcrumbs from '../../Breadcrumbs';
import ButtonComponent from '../../Buttons/ButtonComponent';

import Styles from './styles.module.scss';

const FilingTable = loadable(() => import('./FilingTable'));
const CompanyTable = loadable(() => import('./CompanyTable'));
const DescriptionTable = loadable(() => import('./DescriptionTable'));
const CapitalTable = loadable(() => import('./CapitalTable'));
const FilingTypeSH01 = loadable(() => import('./FilingTypeSH01'));

const cx = classNames.bind(Styles);

export class SingleFiling extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      loadingPage: true,
      mobileShowPDF: false,
      filing: {},
      breadcrumbs: [],
      documentLoading: true,
      documentBlocked: false,
      sourceData: {},
      hideDoc: false,
      fileType: '',
    };
    this.documentFrame = createRef();
    autoBind(this);
  }

  componentDidMount() {
    const { setActiveHref: propsSetActiveHref } = this.props;
    propsSetActiveHref('');
    this.getFillingData();
  }

  async getFillingData() {
    const {
      match: {
        params: {
          filingInfo,
        },
      },
      setActiveDirectories,
    } = this.props;
    const [filingId] = String(filingInfo).split('-');
    const filing = await FilingsSvc.getFilling({
      param: { filing_id: filingId },
    });

    if (!filing) {
      window.location.href = '/404';
      return;
    }

    const { sourceName } = filing.filingInformation || {};
    const { type, companyInformation } = filing;
    const sourceData = sourceDataHelper(sourceName);

    const breadcrumbs = [
      {
        name: 'Directory Home',
        path: 'news/directory',
        onClick: setActiveDirectories,
      },
      {
        name: 'Filings',
        path: 'directory/filings',
      },
    ];

    if (sourceData.id) {
      breadcrumbs.push({
        name: sourceData.parentName,
        path: `directory/filings/${sourceData.parentTicker}`,
      });
      breadcrumbs.push({
        name: sourceData.name,
        path: `directory/filings/${sourceData.parentTicker}/${sourceData.ticker}`,
      });
    }
    if (['SH01', 'SH02', 'CS01'].includes(type) && companyInformation) {
      const objKey = Object.keys(companyInformation);
      if (objKey.length && companyInformation[objKey[0]]) {
        const currentCompanies = companyInformation[objKey[0]];
        breadcrumbs.push({
          name: currentCompanies.companyName,
          path: `${UK_PRIVATE_COMPANY_INSIGHTS_PATH_MAIN}/companies/${currentCompanies.companyNumber}`,
        });
      }
    }
    breadcrumbs.push({
      name: filing.title || '',
      path: window.location.pathname,
    });

    this.setMetaData(filing);
    this.setState({
      loadingPage: false,
      filing,
      breadcrumbs,
      sourceData: sourceData || {},
    }, async () => {
      // true means HTML, false means link
      const filingType = /<\/?[a-z][\s\S]*>/i.test(filing.filing);
      if (filingType) {
        this.setFullTextIntoFrame(this.documentFrame.current);
        this.setState({
          fileType: 'html',
        });
      } else {
        try {
          if (filing.filing.endsWith('/document')) {
            this.documentFrame.current.src = filing.filing;
            this.setState({
              fileType: 'doc',
            });
          } else {
            this.setState({
              documentBlocked: true,
              documentLoading: false,
              fileType: 'link',
            });
          }
        } catch (e) {
          this.setState({
            documentBlocked: true,
            documentLoading: false,
            fileType: 'link',
          });
        }
      }
    });
  }

  setMetaData(filing) {
    const { title, description } = filing;

    if (title) window.document.title = `${title} | CityFalcon`;
    if (description) getMetaTag('name', 'description').setAttribute('content', description);
  }

  setFullTextIntoFrame(frameElement) {
    const { filing } = this.state;

    const frameWindow = frameElement.contentWindow;
    const frameDocument = frameWindow.document;
    frameDocument.open();
    frameDocument.write(filing.filing);
    frameDocument.close();
    frameDocument.addEventListener('load', () => {
      frameDocument.body.style.setProperty('padding', '20px');
      frameDocument.body.style.setProperty('margin', '0');
      if (frameDocument.body.scrollHeight) frameDocument.style.setProperty('height', `${frameDocument.body.scrollHeight}px`);
    }, { once: true });
    this.setState({
      documentLoading: false,
    });
  }

  toggleMobilePDF() {
    const { mobileShowPDF } = this.state;
    const newState = !mobileShowPDF;
    this.setState({ mobileShowPDF: newState });

    if (newState) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('overflow');
    }
  }

  sourceOnLoad() {
    const { fileType } = this.state;
    const currentFrameData = this.documentFrame.current.contentWindow;
    this.setState({
      documentLoading: false,
      hideDoc: fileType === 'doc' && !isMobile ? !currentFrameData.length : false,
    });
  }

  render() {
    const {
      loadingPage,
      mobileShowPDF,
      filing,
      breadcrumbs,
      sourceData,
      documentLoading,
      documentBlocked,
      hideDoc,
      fileType,
    } = this.state;
    const { searchBarActive, permissions } = this.props;
    const {
      filingInformation, companyInformation, descriptionValues,
    } = filing || {};

    const hasInsightsAccess = (permissions.features || []).includes(UK_INSIGHTS);

    if (loadingPage) {
      return (
        <div className={cx('Filings')}>
          <Preloader loading />
        </div>
      );
    }

    return (
      <div className={cx('Filings', searchBarActive && Styles.blurred)}>
        <div className={cx('Filings-details-container')}>
          <Breadcrumbs items={breadcrumbs} base="" />

          <div className={cx('Filings-head')}>
            <span>{filing.title}</span>
          </div>

          <div className={cx('Filings-details')}>
            <div className={cx('Filings-details-header')}>
              File Info
            </div>
            <FilingTable
              filingInformation={filingInformation}
              sourceData={sourceData}
            />
            {companyInformation && <CompanyTable companyInformation={companyInformation} />}

            {Boolean(filing.description) && (
              <div className={cx('Single-table')}>
                <div className={cx('Title')}>Description</div>
                <div className={cx('Body')}>{filing.description}</div>
              </div>
            )}

            <DescriptionTable descriptionValues={descriptionValues || {}} />

            {filing.type === 'SH01' && <CapitalTable descriptionValues={descriptionValues || {}} />}
          </div>

          {filing.type === 'SH01' && filing?.extractedData && <FilingTypeSH01 filing={filing} hasInsightsAccess={hasInsightsAccess} />}

          {!hasInsightsAccess && (
            <div className={cx('Filings-uk-upgrade')}>
              <div className={cx('Filings-uk-upgrade-text')}>
                Unlock full access to structured filings content with
                {' '}
                <span>UK Company Insights.</span>
              </div>
              <div className={cx('Filings-uk-upgrade-btn')}>
                <Link to="/pricing">
                  Upgrade
                </Link>
              </div>
            </div>
          )}

          {
            fileType === 'doc' ? (
              <>
                <div className={Styles.filings_iframe_title}>
                  Original Filing
                </div>
                <div className={Styles.filings_iframe_message}>
                  The file is hosted externally and may take a few seconds to load.
                  If it doesn&apos;t load, please refresh the page
                </div>
              </>
            ) : null
          }

          {!documentBlocked && Boolean(filing.filing) && (
            <span className={cx('full-btn')} onClick={this.toggleMobilePDF}>
              See Full Filing
            </span>
          )}
        </div>
        <div className={cx('Filings-document-loading')}>
          <Preloader loading={documentLoading} />
        </div>

        {Boolean(filing.filing) && (
          <div className="text-center" hidden={documentLoading}>
            {!documentBlocked && !hideDoc ? (
              <div
                className={cx('Filings-document', {
                  'mobile-show': mobileShowPDF,
                })}
              >
                <iframe
                  title={filing.title}
                  onLoad={this.sourceOnLoad}
                  ref={this.documentFrame}
                />
                <FontAwesomeIcon icon={faXmark} onClick={this.toggleMobilePDF} />
              </div>
            ) : (
              <div className={cx('filing_original_button')}>
                <ButtonComponent
                  path={filing.filing}
                  value="See Original Filing"
                  isBlank
                  dynamic
                  align_center
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = ({
  searchReducers, subscriptions,
}) => ({
  searchBarActive: searchReducers.searchBarActive,
  permissions: subscriptions.permissions,
});

const mapToDispatchProps = (dispatch) => ({
  setActiveHref: (href) => dispatch(setActiveHref(href)),
});

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...ownProps,
  ...dispatchProps,
  setActiveDirectories: () => dispatchProps.setActiveHref('/news/directory'),
});

export default withComponentName(
  connect(mapStateToProps, mapToDispatchProps, mergeProps)(SingleFiling),
);
